import { withDevtools } from "@angular-architects/ngrx-toolkit";
import { 
  patchState, 
  signalStore, 
  withMethods, 
  withState 
} from "@ngrx/signals";
import { 
  AIFeatures,
  AI_CONFIG_DEFAULT_ADMIN_DATA,
  AI_CONFIG_DEFAULT_USER_DATA,
  AiUserDataGroup,
} from "@maximizer/core/shared/data-access";

export type AIStoreState = {
  aiUserDataGroup: AiUserDataGroup;
  isEarlyAccess: boolean;
  isCreditSystemEnabled: boolean;
}

const initialState: AIStoreState = {
  aiUserDataGroup: {
    admin: AI_CONFIG_DEFAULT_ADMIN_DATA,
    user: AI_CONFIG_DEFAULT_USER_DATA,
    isAdmin: false,
  },
  isEarlyAccess: true,
  isCreditSystemEnabled: false,
};

export const AIStore = signalStore(
  withDevtools('ai'),
  withState(initialState),
  withMethods((store) => ({
    setCreditsUsed(creditsUsed: number): void {
      patchState(store, () => ({ 
        aiUserDataGroup: { 
          ...store.aiUserDataGroup(), 
          admin: { 
            ...store.aiUserDataGroup().admin,
            creditsUsed
          } 
        } 
      }));
    },
    setCreditsTotal(creditsTotal: number): void {
      patchState(store, () => ({ 
        aiUserDataGroup: { 
          ...store.aiUserDataGroup(), 
          admin: { 
            ...store.aiUserDataGroup().admin, 
            creditsTotal 
          } 
        } 
      }));
    },
    reset(): void {
      patchState(store, () => ({ 
        aiUserDataGroup: { 
          ...store.aiUserDataGroup(), 
          admin: { 
            ...store.aiUserDataGroup().admin, 
            creditsUsed: AI_CONFIG_DEFAULT_ADMIN_DATA.creditsUsed,
            creditsTotal: AI_CONFIG_DEFAULT_ADMIN_DATA.creditsTotal,
          } 
        } 
      }));
    },
    setUserDataGroup(aiUserDataGroup: AiUserDataGroup): void {
      patchState(store, () => ({ aiUserDataGroup }));
    },

    setAiFeaturesEnabled(aiFeaturesEnabled: AIFeatures): void {
      const data = store.aiUserDataGroup().user;
      
      patchState(store, () => ({ 
        aiUserDataGroup: { 
          ...store.aiUserDataGroup(), 
          user: { 
            ...data, 
            aiFeaturesEnabled 
          } 
        } 
      }));
    },
    setAiFeaturesEnabledAdmin(aiFeaturesEnabled: AIFeatures): void {
      const data = store.aiUserDataGroup().admin;
      
      patchState(store, () => ({ 
        aiUserDataGroup: { 
          ...store.aiUserDataGroup(), 
          admin: { 
            ...data, 
            aiFeaturesEnabled 
          } 
        } 
      }));
    },

    updateAiFeatureEnabled(feature: keyof AIFeatures, enabled: boolean): void {
      const data = store.aiUserDataGroup().user;
      
      const updatedFeatures = { 
        ...data.aiFeaturesEnabled, 
        [feature]: enabled 
      };
      
      patchState(store, () => ({ 
        aiUserDataGroup: { 
          ...store.aiUserDataGroup(), 
          user: {
            ...data,
            aiFeaturesEnabled: updatedFeatures
          }
        } 
      }));
    },
    updateAiFeatureEnabledAdmin(feature: keyof AIFeatures, enabled: boolean): void {
      const data = store.aiUserDataGroup().admin;
      
      const updatedFeatures = { 
        ...data.aiFeaturesEnabled, 
        [feature]: enabled 
      };
      
      patchState(store, () => ({ 
        aiUserDataGroup: { 
          ...store.aiUserDataGroup(), 
          admin: {
            ...data,
            aiFeaturesEnabled: updatedFeatures
          }
        } 
      }));
    },
  })),
);

export type AIStore = InstanceType<typeof AIStore>; 